/**
 * VoxSmyth Token Service (Phase 2 — cookie-first)
 *
 * Tokens now live in httpOnly cookies set by the backend, which cannot be
 * accessed by JS (XSS-safe). This module stays in place because:
 *   - It still owns the cached `user` / `admin` metadata (NOT the token itself)
 *   - The `setToken` / `getToken` API is called from 50+ legacy sites — keeping
 *     it as a no-op avoids touching every caller and silently lets the cookie
 *     path work
 *
 * On first load after the Phase 2 deploy we wipe any legacy token bytes that
 * may still be sitting in localStorage from before the migration.
 */

const TOKEN_KEY = 'vs_t';
const ADMIN_TOKEN_KEY = 'vs_at';
const USER_KEY = 'vs_u';
const ADMIN_USER_KEY = 'vs_au';

// Backend URL for logout cookie clearing (Phase 1 cookie migration).
const _BACKEND_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';

// Fire-and-forget POST that tells the backend to clear its httpOnly cookie.
// We don't await it because every clearToken/clearAdmin caller is synchronous
// and we don't want to block UI navigation on a network round-trip.
const _serverClearCookie = (path) => {
  if (!_BACKEND_URL) return;
  try {
    fetch(`${_BACKEND_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,  // survive page unload (logout often immediately navigates)
    }).catch((err) => {
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[tokenService] cookie clear failed (non-fatal):', err && err.message);
      }
    });
  } catch (err) {
    // fetch() not available or threw synchronously — non-fatal.
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[tokenService] cookie clear threw:', err && err.message);
    }
  }
};

const _encode = (val) => {
  try { return btoa(encodeURIComponent(val)); } catch { return val; }
};
const _decode = (val) => {
  try { return decodeURIComponent(atob(val)); } catch { return val; }
};

// One-time wipe: nuke any token bytes that pre-Phase-2 deploys may have left in
// browser storage. The cookie is the only source of truth now. User METADATA
// (name, email, tier) lives in separate keys and is preserved.
const _wipeLegacyTokenStorage = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch (err) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[tokenService] legacy token wipe skipped:', err && err.message);
    }
  }
};
_wipeLegacyTokenStorage();

const tokenService = {
  // ── User Auth ──────────────────────────────
  // Token now lives in an httpOnly cookie. JS cannot read it (XSS-safe).
  // getToken() returns null so the old `Bearer ${token}` callsites send
  // `Bearer null`, which the backend ignores in favor of the cookie.
  getToken: () => null,

  // setToken is a no-op. The backend has already set the httpOnly cookie on
  // login; there is nothing for the client to store.
  setToken: () => {},

  clearToken: () => {
    // Belt-and-suspenders: keep wiping the legacy key in case anything else
    // wrote to it during this session.
    try { localStorage.removeItem(TOKEN_KEY); } catch (e) { /* noop */ }
    _serverClearCookie('/api/logout');
  },

  // ── User Data ──────────────────────────────
  // User metadata (name/email/tier) is not sensitive and is kept in
  // localStorage so we don't have to round-trip to /api/user/profile on every
  // navigation.
  getUser: () => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(_decode(raw)); } catch { return null; }
  },

  setUser: (user) => {
    if (!user) return;
    localStorage.setItem(USER_KEY, _encode(JSON.stringify(user)));
  },

  clearUser: () => {
    localStorage.removeItem(USER_KEY);
  },

  // ── Admin Auth (cookie-backed) ─────────────
  // Same logic as user auth — token is in an httpOnly cookie.
  getAdminToken: () => null,

  setAdminToken: () => {},

  getAdminUser: () => {
    const raw = sessionStorage.getItem(ADMIN_USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(_decode(raw)); } catch { return null; }
  },

  setAdminUser: (user) => {
    if (!user) return;
    sessionStorage.setItem(ADMIN_USER_KEY, _encode(JSON.stringify(user)));
  },

  clearAdmin: () => {
    try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); } catch (e) { /* noop */ }
    sessionStorage.removeItem(ADMIN_USER_KEY);
    _serverClearCookie('/api/admin/logout');
  },

  // ── Full Logout ────────────────────────────
  clearAll: () => {
    tokenService.clearToken();
    tokenService.clearUser();
    tokenService.clearAdmin();
  },
};

export default tokenService;
